import posthog from '@leyan/lytics';
import { message } from 'antd';
import { useCallback, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import {
  generateAuthUrl,
  GenerateTikTokAuthUrlParams,
  generateTiktokUSAuthUrl,
  TiktokStandaloneAuthResultBody,
  tiktokStandaloneResult,
  tiktokUSStandaloneResult,
} from 'services/api/tiktok';
import useOpenURL from './useOpenURL';
import { useTransAppProductName } from 'hooks/useTrans';

const useTiktokAuthShop = (isUS = false) => {
  const { t } = useTranslate();
  const appProductNameT = useTransAppProductName();

  const [loading, setLoading] = useState<boolean>(false);
  const [uuids, setUuids] = useState<string[]>([]);
  const [result, setResult] = useState<TiktokStandaloneAuthResultBody>();

  const openURL = useOpenURL();

  const getTiktokAuthURL = useCallback(
    async (params: GenerateTikTokAuthUrlParams) => {
      try {
        setLoading(true);
        const fn = isUS ? generateTiktokUSAuthUrl : generateAuthUrl;
        const url = await fn(params);
        setLoading(false);

        const u = new URL(url);
        const uuid = u.searchParams.get('state');
        if (uuid) {
          setUuids((u) => [...u, uuid]);
          return url;
        }
        throw new Error('请求 Tiktok 授权链接失败');
      } catch (err) {
        setLoading(false);
        message.error(t('authShop.获取TikTok授权链接失败'));
        return undefined;
      }
    },
    [t, isUS],
  );

  const copyTiktokURL = useCallback(
    async (params: GenerateTikTokAuthUrlParams, noPrefix = false) => {
      try {
        const authURL = await getTiktokAuthURL(params);
        if (authURL) {
          const template = noPrefix
            ? authURL
            : appProductNameT('authShop.copyLinkTemplate3', { platform: 'TikTok' }) + authURL;
          await navigator.clipboard.writeText(template);
          message.success(t('authShop.复制成功'));
          return true;
        }
        throw new Error('请求失败');
      } catch (err) {
        message.error(t('authShop.复制链接失败'));
        return false;
      }
    },
    [getTiktokAuthURL, t, appProductNameT],
  );

  const openAuthUrl = useCallback(
    async (params: GenerateTikTokAuthUrlParams) => {
      try {
        const authURL = await getTiktokAuthURL(params);
        if (authURL) {
          openURL(authURL, params.openMode);
          return true;
        }
        throw new Error('请求失败');
      } catch (err) {
        message.error(t('authShop.打开链接失败'));
        return false;
      }
    },
    [getTiktokAuthURL, openURL, t],
  );

  const checkAuthReulst = useCallback(
    (uuid: string) => {
      const fn = isUS ? tiktokUSStandaloneResult : tiktokStandaloneResult;
      return fn(uuid)
        .then((r) => r)
        .catch((e) => {
          posthog.capture('$authReulstError', {
            platform: isUS ? 'tiktok_us' : 'tiktok',
            error: e,
          });
          return null;
        });
    },
    [isUS],
  );

  return {
    loading,
    openAuthUrl,
    checkAuthReulst,
    uuids,
    setUuids,
    result,
    setResult,
    copyTiktokURL,
  };
};

export default useTiktokAuthShop;
